import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LOCALE_STORAGE_KEY, LocaleContext, type LocaleContextValue } from './context';
import { DEFAULT_LOCALE, INTL_LOCALE, isLocale, type Dictionary, type Locale } from './dictionary';
import { en } from './en';
import { tr } from './tr';

/**
 * Dil sağlayıcı.
 *
 * Context BURADA doğru araç: dil nadiren değişir ve neredeyse her bileşen
 * okur — yani Context'in zayıf olduğu "sık güncellenen paylaşımlı durum"
 * senaryosunun tam tersi.
 *
 * Sunucu verisi gibi TanStack Query'ye, UI durumu gibi Zustand'a da konmadı:
 * dil bir kullanıcı tercihi ve sayfa yenilendiğinde korunması gerekiyor;
 * `localStorage` bu tercihin doğal yeri.
 */

const DICTIONARIES: Record<Locale, Dictionary> = { tr, en };

/**
 * Başlangıç dilini çözer: kayıtlı tercih → tarayıcı dili → varsayılan.
 *
 * `localStorage` erişimi try/catch içinde: gizli sekmede veya depolama izni
 * kapalı bir tarayıcıda erişimin kendisi FIRLATIR ve uygulama hiç açılmazdı.
 */
function resolveInitialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch {
    // Depolama yoksa tarayıcı diline düşmek yeterli.
  }

  const browserLanguage = window.navigator.language.slice(0, 2).toLowerCase();
  return isLocale(browserLanguage) ? browserLanguage : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(resolveInitialLocale);

  /* `lang` özniteliği kozmetik değil: ekran okuyucular telaffuzu, tarayıcılar
     tireleme ve yazım denetimini buna göre seçer; CSS `uppercase` da ona
     bakar — `lang="tr"` kalırsa İngilizce "Functional" "FUNCTİONAL" olur.
     İLK açılışta da yazılmalı: `index.html` `tr` ile gelir, kayıtlı tercih
     `en` olsa bile. */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);

    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // Tercih kaydedilemiyorsa oturum boyunca geçerli kalır; hata gösterilmez.
    }
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      intlLocale: INTL_LOCALE[locale],
      t: DICTIONARIES[locale],
      setLocale,
    }),
    [locale, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}
